import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  bufferCount,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { sum } from 'lodash';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FLAGS, FlagsService } from './flags.service';
import { TelemetryService } from './telemetry.service';

export enum PerformanceTier {
  tierOne = 'Tier 1', // CPU_real < 15 the device is performing well
  tierTwo = 'Tier 2', // CPU_real >= 15
  tierThree = 'Tier 3', // CPU_real >= 50
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CpuMonitoringService {
  private currentTier = new BehaviorSubject<PerformanceTier>(PerformanceTier.tierOne);
  private cpuUsageObservable: Observable<number>;
  private disableAutoRevert = new BehaviorSubject<boolean>(false);

  get disableAutoRevert$(): Observable<boolean> {
    return this.disableAutoRevert.asObservable();
  }

  get currentTier$(): Observable<PerformanceTier> {
    return this.currentTier.asObservable();
  }

  get currentTierValue(): PerformanceTier {
    return this.currentTier.getValue();
  }

  constructor(private flagsService: FlagsService, private telemetry: TelemetryService) {
    this.telemetry.setSessionVars({
      current_cpu_tier: PerformanceTier.tierOne,
    });

    this.cpuUsageObservable = this.telemetry.currPerfMetrics$.pipe(
      untilDestroyed(this),
      filter((record) => !record.interrupted && record.cpu?.p50 !== undefined),
      map((record) => record.cpu.p50),
    );

    this.flagsService
      .featureFlagChanged(FLAGS.PERFORMANCE_INTERVENTION_CPU)
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter((isCpuPerformanceInterventionEnabled) => isCpuPerformanceInterventionEnabled),
      )
      .subscribe(() => {
        // the window is the number of performance scenarios to average upon for upgrading/downgrading the cpu tier
        // currently each performance scenario takes 10 seconds (as stated in LOG_INTERVAL at performance.logger.service).
        const downgradeSamplingWindow =
          (this.flagsService.featureFlagsVariables?.performance_intervention_cpu
            ?.downgrade_sample_window as number) ?? 3; // the default is averaging over 30 seconds;

        const upgradeSamplingWindow =
          (this.flagsService.featureFlagsVariables?.performance_intervention_cpu
            ?.upgrade_sample_window as number) ?? 6; // the default is averaging over 60 seconds;

        const bufferSize = Math.max(upgradeSamplingWindow, downgradeSamplingWindow);

        this.disableAutoRevert$
          .pipe(
            untilDestroyed(this),
            switchMap((isDisabled) =>
              isDisabled ? EMPTY : this.cpuUsageObservable.pipe(bufferCount(bufferSize, 1)),
            ),
          )
          .subscribe((cpuUsageArray) => {
            // when unsubscribing (due to object destroyed)
            // the observable emits the buffered array while emptying it
            // resulting in a number of emissions related to the number of elements in the buffer array
            if (cpuUsageArray.length !== bufferSize) {
              return;
            }
            // removing the outliers (max and min values) from the array before averaging
            // to get a value that is less sensitive to noise.
            cpuUsageArray = cpuUsageArray.sort((a, b) => a - b);
            cpuUsageArray = cpuUsageArray.slice(1, cpuUsageArray.length - 1);

            if (cpuUsageArray.length) {
              const upgradeWindowSample = cpuUsageArray.slice(-upgradeSamplingWindow);
              const upgradeWindowCpuAverage = sum(upgradeWindowSample) / upgradeWindowSample.length;
              const upgradedTier = this.calculateCurrentCpuTier(upgradeWindowCpuAverage);

              const downgradeWindowSample = cpuUsageArray.slice(-downgradeSamplingWindow);
              const downgradeWindowCpuAverage =
                sum(downgradeWindowSample) / downgradeWindowSample.length;
              const downgradedTier = this.calculateCurrentCpuTier(downgradeWindowCpuAverage);

              if (this.compareTiers(downgradedTier, this.currentTierValue) === -1) {
                this.setCurrentTier(downgradedTier);
              } else if (this.compareTiers(upgradedTier, this.currentTierValue) === 1) {
                this.setCurrentTier(upgradedTier);
              }
            }
          });
      });
  }

  compareTiers(tierA: PerformanceTier, tierB: PerformanceTier): number {
    const tiersToNumbers = {
      [PerformanceTier.tierOne]: 3,
      [PerformanceTier.tierTwo]: 2,
      [PerformanceTier.tierThree]: 1,
    };
    if (tiersToNumbers[tierA] > tiersToNumbers[tierB]) {
      return 1;
    } else if (tiersToNumbers[tierA] < tiersToNumbers[tierB]) {
      return -1;
    } else {
      return 0;
    }
  }

  forceCpuTier(cpuTier: PerformanceTier): void {
    this.setCurrentTier(cpuTier);
  }

  toggleDisableAutoRevert() {
    const newValue = !this.disableAutoRevert.getValue();
    this.disableAutoRevert.next(newValue);
  }

  private calculateCurrentCpuTier(cpuAvg: number): PerformanceTier {
    const tierTwoThreshold =
      (this.flagsService.featureFlagsVariables?.performance_intervention_cpu
        ?.threshold_tier_2 as number) ?? 15;
    const tierThreeThreshold =
      (this.flagsService.featureFlagsVariables?.performance_intervention_cpu
        ?.threshold_tier_3 as number) ?? 50;

    let currentCpuTier;
    if (cpuAvg < tierTwoThreshold) {
      currentCpuTier = PerformanceTier.tierOne;
    } else if (cpuAvg >= tierTwoThreshold && cpuAvg < tierThreeThreshold) {
      currentCpuTier = PerformanceTier.tierTwo;
    } else {
      currentCpuTier = PerformanceTier.tierThree;
    }
    return currentCpuTier;
  }

  private setCurrentTier(newTier: PerformanceTier): void {
    const currentTierValue = this.currentTier.getValue();

    if (currentTierValue !== newTier) {
      this.currentTier.next(newTier);
      console.log(`[PERF MITIGATIONS] CPU tier upgrade/downgrade: ${currentTierValue},${newTier}`);

      this.telemetry.event('performance_cpu_tier_change', {
        previous_cpu_tier: currentTierValue,
        current_cpu_tier: newTier,
      });
      this.telemetry.setSessionVars({ current_cpu_tier: newTier });
    }
  }
}
